<template>
  <b-row class="w-100">
    <b-col class="d-flex flex-column align-items-center w-100 p-0">
      <base-card-indicator variant="info">
        <div class="d-flex align-items-center justify-content-between">
          <h5 ref="text__part-title" class="font-weight-bold text__part-title mb-0">
            {{ title }}
          </h5>
        </div>
      </base-card-indicator>

      <b-row class="mt-3 w-100">
        <b-col cols="12" class="mt-3 d-flex justify-content-center p-0">
          <b-card no-body class="card-box card__comfirm w-100">
            <b-card-body class="pt-2">
              <b-alert
                ref="alert__evaluate"
                :show="!canAssess"
                variant="warning"
                class="my-3 alert__evaluate"
              >
                <b-img :src="iconAlert" class="icon__alert" />
                <span ref="alert__evaluate-text">{{
                  `You have to complete all evaluation parts and provide feedback before submitting the results.`
                }}</span></b-alert
              >
              <!-- Assessor: show agreements -->
              <div v-if="assessorId">
                <div
                  v-for="agreement in agreements"
                  :key="`agreement-${agreement.key}`"
                  class="mt-3"
                >
                  <div class="font-weight-bold">{{ agreement.title }}</div>
                  <div>{{ agreement.description }}</div>
                </div>
              </div>

              <!-- Issuer: show consent form -->
              <consent-agreement-form
                v-if="issuerId"
                ref="consent-agreement-form"
                v-model="isAgreed"
                :topic="consentTopic"
                :cache-key="`${evidenceSubmission.id}`"
                :agreements="agreements"
                :disabled="!canAssess || submitting"
              ></consent-agreement-form>
            </b-card-body>

            <b-card-footer>
              <b-button
                ref="button__submit-evaluation"
                variant="success"
                class="button__submit-evaluation"
                :disabled="submitting || !canAssess || (issuerId && !isAgreed)"
                @click="activeModal = true"
                >{{ issuerId ? 'Submit' : 'Submit and continue' }}</b-button
              >
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
    </b-col>

    <!-- Confirm modal -->

    <base-modal ref="modal__confirm" :show.sync="activeModal">
      <template #modal-header>
        <h5>Evaluation Confirmation</h5>
      </template>

      <div class="text-black-50 mb-2">
        Are you sure you want to submit this evaluation ? After you confirmed you can’t edit this
        evaluation anymore.
      </div>
      <b-alert variant="primary" show>
        <div class="font-weight-bold">
          <b ref="modal__confirm-submission-id">Submission ID: {{ evidenceSubmission.code }}</b>
          <br />
          <b ref="modal__confirm-mc-name">MC Title: {{ learningUnit.name }}</b>
        </div>
      </b-alert>

      <div class="text-black-50">Click at the “Confirm” button to submit this evaluation.</div>

      <template #modal-footer>
        <b-form id="form-submit-evidence" @submit.prevent="submit">
          <b-button
            variant="link"
            class="btn-link-dark"
            :disabled="submitting"
            @click="activeModal = false"
          >
            Cancel
          </b-button>

          <b-button type="submit" variant="success" :disabled="submitting">
            <span v-if="!submitting">Confirm</span>

            <span v-else class="pr-2 pl-2">
              <b-spinner label="Loading..." variant="secondary" small />
            </span>
          </b-button>
        </b-form>
      </template>
    </base-modal>
  </b-row>
</template>

<script>
import BaseCardIndicator from '@/components/base-components/BaseCardIndicator';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import LearningUnit from '@/models/LearningUnit';
import BaseModal from '@/components/base-components/BaseModal';
import ConsentAgreementForm from '@/components/Consent/ConsentAgreementForm';
import iconAlert from '@/assets/icon-alert/icon-circle-exclamation-white.svg';

export default {
  components: {
    BaseCardIndicator,
    BaseModal,
    ConsentAgreementForm
  },

  props: {
    learningUnit: {
      type: LearningUnit,
      required: true
    },

    evidenceSubmission: {
      type: EvidenceSubmission,
      required: true
    },

    allowAssess: {
      type: Boolean,
      default: false
    },

    assessorId: {
      type: [String, Number],
      default: null
    },

    issuerId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      submitting: false,
      submitted: false,
      activeModal: false,
      isAgreed: false,
      nextSubmission: null,
      agreements: [
        {
          key: 'CONFIRM_EVALUATION',
          title: 'Confirm Evaluation',
          description:
            'I confirm that the submission is assessed according to the assessment criteria specified in the Micro-credential, without considering any other criteria beyond those stated.'
        },
        {
          key: 'CONFIRM_FEEDBACK_AND_COMMENT',
          title: 'Confirm Feedback and Comment',
          description:
            'I confirm that I have provided specific and clear feedback on all aspects in every comment section.'
        }
      ],
      iconAlert
    };
  },

  computed: {
    canAssess() {
      if (this.assessorId)
        return (
          this.allowAssess &&
          this.evidenceSubmission.canAssess &&
          this.evidenceSubmission.isReviewPendingByAssessor
        );
      if (this.issuerId)
        return (
          this.allowAssess &&
          this.evidenceSubmission.canGrant &&
          this.evidenceSubmission.isReviewPendingByIssuer
        );
      return false;
    },

    consentTopic() {
      let topic = 'kmutt4life__agreement-submit-assess-evidence';
      if (this.assessorId) topic = topic.concat('-assessor');
      if (this.issuerId) topic = topic.concat('-issuer');
      return topic;
    },

    title() {
      let title = "Assessor's Confirmation";
      if (this.issuerId) title = "Issuer's Confirmation";
      return title;
    }
  },

  watch: {
    canAssess(value) {
      if (!value) {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
      }
    }
  },

  async mounted() {
    await this.fetchPendingSubmissions();
    if (!this.canAssess) {
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    }
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    handleBeforeUnload(event) {
      const alreadyReviewed = this.assessorId
        ? Boolean(this.evidenceSubmission.assessor_reviewed_at)
        : Boolean(this.evidenceSubmission.issuer_reviewed_at);

      if (this.submitted || alreadyReviewed) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    },
    async submit() {
      this.$emit('isEvidenceSubmitting', true);
      this.submitting = true;

      if (this.assessorId && !this.evidenceSubmission.assessor_reviewed_at) {
        await this.updateAssessByAssessor();
      }

      if (this.issuerId && !this.evidenceSubmission.issuer_reviewed_at) {
        await this.grantByIssuer();
      }

      this.activeModal = false;
      this.$emit('isEvidenceSubmitting', false);
      this.submitting = false;
    },

    async updateAssessByAssessor() {
      try {
        await EvidenceSubmission.api().assessByAssessor(this.evidenceSubmission.id, {
          pass: this.evidenceSubmission.pass,
          feedback_for_earner: this.evidenceSubmission.feedback_for_earner,
          comment_for_issuer: this.evidenceSubmission.comment_for_issuer,
          assessor_recommendation: this.evidenceSubmission.assessor_recommendation
        });

        this.submitted = true;
        this.$emit('assessed');
        this.$toast.success('Your evaluation has been submitted successfully');
        await this.navigateAfterAssessment();
      } catch (error) {
        if (error.response.status === 403) {
          this.$toast.error(error.response.data.message);
          this.$router.push({
            name: 'design-planning/info',
            params: { learningUnitId: this.learningUnit.id },
            query: { tab: 'assess' }
          });
          return;
        }

        return this.$toast.error('Cannot submit your evaluation, please try again');
      }
    },

    async fetchPendingSubmissions() {
      // Fetch pending submissions for the current learning unit
      await EvidenceSubmission.api().fetchAssessor(this.learningUnit.id);

      // Find next pending submission (excluding current one, must be latest, assigned to current assessor)
      const pendingSubmissions = EvidenceSubmission.query()
        .where('learning_unit_id', this.learningUnit.id)
        .where('assessor_id', this.assessorId)
        .where('assessor_reviewed_at', null)
        .where((submission) => submission.id !== this.evidenceSubmission.id)
        .where('status', 'REVIEW_PENDING')
        .where('is_latest', true)
        .orderBy('submitted_at', 'desc')
        .get();

      this.nextSubmission = pendingSubmissions.length > 0 ? pendingSubmissions[0] : null;
    },

    navigateAfterAssessment() {
      if (this.nextSubmission) {
        // Navigate to next pending submission
        this.$router.push({
          name: 'learning-unit/evidence/assess',
          params: {
            learningUnitId: this.learningUnit.id,
            learnerId: this.nextSubmission.user_id,
            evidenceSubmissionId: this.nextSubmission.id
          }
        });
      } else {
        // No more pending submissions, navigate to catalog evaluate page
        window.location.href = new URL('/en/evaluate', process.env.VUE_APP_CATALOG_APP_URL).toString();
      }
    },

    async grantByIssuer() {
      try {
        await EvidenceSubmission.api().grant(this.evidenceSubmission.id, {
          pass: this.evidenceSubmission.pass,
          feedback_for_earner: this.evidenceSubmission.feedback_for_earner,
          comment_for_issuer: this.evidenceSubmission.comment_for_issuer
        });

        this.submitted = true;
        this.$emit('assessed');
        this.$toast.success('Your evaluation has been granted successfully');
      } catch (error) {
        console.log(error);
        this.$toast.error('Cannot granted your evaluation, please try again');
      }
    }
  }
};
</script>

<style lang="scss">
.card__comfirm {
  max-width: 910px;
}

.alert__evaluate {
  display: flex;
  gap: 16px;
  align-items: flex-start;

  .icon__alert {
    color: #ffffff;
    background-color: #824224;
    padding: 6px;
    border-radius: 8px;
    img {
      width: 18px;
    }
  }
}
</style>
